import { useEffect, useState } from 'react';
import { ApiError, call } from '../api';

interface Status { configured: boolean; username: string | null; scrobbling: boolean; sessionUser: string | null; hasSecret: boolean }

/** Last.fm per profiel: eigen API-sleutel (aanbevelingen, hitlijsten) en optioneel scrobbelen. */
export function LastfmSettings() {
  const [st, setSt] = useState<Status | null>(null);
  const [form, setForm] = useState({ apiKey: '', apiSecret: '', username: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => call<Status>('/api/lastfm').then((s) => {
    setSt(s);
    setForm((f) => ({ ...f, username: s.username ?? '' }));
  }).catch(() => setSt(null));

  useEffect(() => {
    void load();
    const q = new URLSearchParams(location.search).get('lastfm');
    if (q === 'ok') setMsg('✓ Scrobbelen staat aan.');
    if (q === 'fout') setMsg('Koppelen met Last.fm is niet gelukt. Controleer je gedeelde geheim en probeer opnieuw.');
  }, []);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await call('/api/lastfm', { method: 'PUT', body: JSON.stringify(form) });
      setForm((f) => ({ ...f, apiKey: '', apiSecret: '' }));
      setMsg('✓ Opgeslagen.');
      await load();
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    try {
      const r = await call<{ url: string }>('/api/lastfm/connect');
      location.href = r.url;
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Koppelen mislukt.');
    }
  };

  return (
    <form className="settings-block form-grid" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <div className="account-head">
        <h2>Last.fm</h2>
        <span className={`status-pill ${st?.configured ? 'ok' : 'warn'}`}>{st?.configured ? (st.scrobbling ? `Scrobbelt als ${st.sessionUser}` : 'Gekoppeld') : 'Niet gekoppeld'}</span>
      </div>
      <p className="subtle hint">
        Voor "Aanbevolen voor jou" en de Last.fm-hitlijsten in Muziek. Alleen voor dit profiel. Maak een eigen API-account op
        {' '}<a href="https://www.last.fm/api/account/create" target="_blank" rel="noreferrer noopener">last.fm/api/account/create</a> en vul de sleutel hier in.
        Met het gedeelde geheim erbij kun je ook scrobbelen: wat je in tumedia luistert komt dan op je Last.fm-profiel.
        De sleutels worden versleuteld op je eigen server bewaard.
      </p>
      <label>Gebruikersnaam op Last.fm<input className="text-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoComplete="off" /></label>
      <label>API-sleutel<input className="text-input" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} placeholder={st?.configured ? 'Opgeslagen — alleen invullen om te wijzigen' : '32 tekens'} autoComplete="off" spellCheck={false} /></label>
      <label>Gedeeld geheim (optioneel, voor scrobbelen)<input className="text-input" type="password" value={form.apiSecret} onChange={(e) => setForm({ ...form, apiSecret: e.target.value })} placeholder={st?.hasSecret ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="off" /></label>
      {msg && <p className="subtle" role="status">{msg}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={busy || (!form.apiKey && !st?.configured)}>{busy ? 'Controleren…' : 'Opslaan'}</button>
        {st?.configured && st.hasSecret && !st.scrobbling && <button className="btn" type="button" onClick={() => void connect()}>Scrobbelen aanzetten</button>}
        {st?.configured && <button className="btn btn-ghost" type="button" onClick={async () => { await call('/api/lastfm', { method: 'DELETE' }).catch(() => undefined); setMsg('Ontkoppeld.'); void load(); }}>Ontkoppelen</button>}
      </div>
    </form>
  );
}
